import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Apple, Play, Flame, Wallet } from "lucide-react";
import { toast } from "sonner";
import { WaitlistForm } from "@/components/Waitlist";

function DualOrb() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;
    const draw = () => {
      t += 0.006;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.3;

      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 2.4);
      g.addColorStop(0, "rgba(16,185,129,0.12)");
      g.addColorStop(1, "rgba(16,185,129,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      const rings = [
        { color: "rgba(16,185,129,0.85)", speed: 1, tilt: 0.5, phase: 0 },
        { color: "rgba(14,165,233,0.6)", speed: -0.7, tilt: -0.38, phase: 1.6 },
      ];
      rings.forEach((r) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(Math.sin(t * r.speed + r.phase) * r.tilt);
        ctx.beginPath();
        for (let a = 0; a <= Math.PI * 2 + 0.01; a += 0.02) {
          const wobble = 1 + Math.sin(a * 3 + t * 2 * r.speed) * 0.035;
          const x = Math.cos(a) * R * wobble;
          const y = Math.sin(a) * R * 0.42 * wobble;
          if (a === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 1.2 * dpr;
        ctx.shadowColor = r.color;
        ctx.shadowBlur = 16 * dpr;
        ctx.stroke();
        ctx.restore();
      });

      [
        { color: "#10B981", speed: 0.9, radius: R, squash: 0.42, phase: 0 },
        { color: "#0EA5E9", speed: -0.55, radius: R * 1.12, squash: 0.38, phase: 2.4 },
      ].forEach((d) => {
        const a = t * d.speed + d.phase;
        const x = cx + Math.cos(a) * d.radius;
        const y = cy + Math.sin(a) * d.radius * d.squash;
        ctx.beginPath();
        ctx.arc(x, y, 3.2 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.shadowColor = d.color;
        ctx.shadowBlur = 22 * dpr;
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="h-full w-full" data-testid="hero-orb-canvas" />;
}

const LINES = ["Calories are", "currency.", "Meet Fitnance."];

const StoreButton = ({ icon: Icon, store, badge, toastTitle, toastDesc, highlight, testId }) => (
  <button
    data-testid={testId}
    onClick={() => toast.info(toastTitle, { description: toastDesc })}
    className={`group flex items-center gap-3 rounded-2xl border px-5 py-3 transition-all duration-300 hover:scale-[1.03] ${
      highlight
        ? "border-emerald-500/40 bg-emerald-500/[0.08] hover:border-emerald-400/60 hover:bg-emerald-500/[0.12] hover:shadow-[0_0_24px_rgba(16,185,129,0.25)]"
        : "border-white/10 bg-white/[0.03] hover:border-emerald-500/40 hover:bg-emerald-500/[0.06]"
    }`}
  >
    <Icon className="h-6 w-6 text-slate-100 transition-colors duration-300 group-hover:text-emerald-400" />
    <span className="text-left">
      <span className={`block font-mono text-[9px] uppercase tracking-[0.25em] ${highlight ? "text-emerald-400" : "text-neutral-500"}`}>
        {badge}
      </span>
      <span className="block text-sm font-semibold text-slate-100">{store}</span>
    </span>
  </button>
);

export default function KineticHero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const orbX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const orbY = useTransform(sy, [-0.5, 0.5], [-14, 14]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      onMouseMove={onMouseMove}
      className="relative overflow-hidden pb-24 pt-36 sm:pt-44"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90"
          >
            Now live on the App Store — Google Play soon
          </motion.p>

          <h1
            data-testid="hero-headline-text"
            className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    delay: 0.25 + i * 0.14,
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                  }}
                  className={`block ${i === 1 ? "text-emerald-400" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl text-lg leading-relaxed text-neutral-400 sm:text-xl"
          >
            The only app that tracks your calories and your capital in one calm
            interface. Spend energy and money with the same discipline.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <StoreButton
              icon={Apple}
              store="App Store"
              badge="Now live · Download"
              highlight
              toastTitle="Fitnance is live on the App Store"
              toastDesc="Search “Fitnance” on the App Store to download it today."
              testId="coming-soon-app-store-button"
            />
            <StoreButton
              icon={Play}
              store="Google Play"
              badge="Coming soon"
              toastTitle="Google Play is up next"
              toastDesc="Android is almost here — join the waitlist and we'll email you the moment it's live."
              testId="coming-soon-play-store-button"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md"
          >
            <WaitlistForm
              inputTestId="hero-email-input"
              submitTestId="hero-email-submit-button"
              successTestId="hero-waitlist-success"
            />
          </motion.div>
        </div>

        <motion.div
          style={{ x: orbX, y: orbY }}
          className="relative h-[380px] sm:h-[460px] lg:col-span-5"
        >
          <DualOrb />
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-0 top-10 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#111318]/80 px-4 py-3 backdrop-blur-md"
          >
            <Flame className="h-5 w-5 text-emerald-400" />
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Energy today</p>
              <p className="text-sm font-bold text-slate-100">2,140 kcal</p>
            </div>
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-10 right-0 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#111318]/80 px-4 py-3 backdrop-blur-md"
          >
            <Wallet className="h-5 w-5 text-sky-400" />
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Saved this month</p>
              <p className="text-sm font-bold text-slate-100">$1,280</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
